import { rpc } from '@/api/rpc'
import { safeName, uploadToStorage } from '@/lib/files'
import { db } from '@/offline/db'
import { PermanentError, enqueue, flushOutbox, registerOutboxHandler } from '@/offline/outbox'
import { requestBackgroundSync } from '@/offline/sync'
import { registerVisitHandlers } from '@/features/visits/visits'
import { registerActionHandlers } from './actions'
import { completeness } from './completeness'
import { draftFiles, toSubmitPayload, type UploadedFile } from './to-payload'
import { draftTitle, type ReluxDraft } from './types'

export type SubmitJob = { draftId: string; userId: string }

const CATEGORY_FOR_TYPE = (type: string) =>
  type.startsWith('image/') ? 'Photo' : type.startsWith('audio/') ? 'Voice Note' : 'Other'

/**
 * Hand a finished draft to the outbox. It sends itself when there is signal: photos and files
 * upload first (resumable), then the Relux is submitted with a device-made id so a retry can never
 * create a second request.
 */
export async function queueReluxSubmit(draft: ReluxDraft, userId: string) {
  await db.drafts.update(draft.clientUuid, { status: 'queued', data: draft, updatedAt: Date.now() })
  await enqueue({
    id: draft.clientUuid,
    type: 'relux.submit',
    label: draft.type === 'amendment' ? draftTitle(draft) : `Relux for ${draftTitle(draft)}`,
    userId,
    payload: { draftId: draft.clientUuid, userId } satisfies SubmitJob,
  })
  void requestBackgroundSync()
  void flushOutbox({ userId })
}

export function registerReluxHandlers() {
  registerActionHandlers()
  registerVisitHandlers()
  registerOutboxHandler('relux.submit', async (item) => {
    const { draftId, userId } = item.payload as SubmitJob
    const record = await db.drafts.get(draftId)
    if (!record) throw new PermanentError('The saved Relux could not be found on this device.')
    const draft = record.data as ReluxDraft

    const refs = draftFiles(draft)
    const uploaded: UploadedFile[] = []
    let missing = 0
    for (const ref of refs) {
      const local = await db.files.get(ref.fileId)
      if (!local) {
        missing++
        continue
      }
      const path =
        local.uploadedPath ??
        `uploads/${userId}/${draft.clientUuid}/${local.id}-${safeName(local.name)}`
      if (!local.uploadedPath) {
        await uploadToStorage(path, local.blob, local.type)
        await db.files.update(local.id, { uploadedPath: path })
      }
      uploaded.push({
        fileId: local.id,
        storagePath: path,
        name: local.name,
        mime: local.type,
        size: local.size,
        category: ref.category || CATEGORY_FOR_TYPE(local.type),
        spaceClientId: ref.spaceClientId,
        markup: local.markup,
      })
    }

    const photos = uploaded.filter((f) => f.category === 'Photo').length
    const body = toSubmitPayload(draft, uploaded, completeness(draft, photos).score)
    if (missing > 0) {
      body.notes = [
        body.notes,
        `${missing} file(s) could not be found on the device and were not sent.`,
      ]
        .filter(Boolean)
        .join('\n\n')
    }
    await rpc('submit_relux', { payload: body })

    await db.files.bulkDelete(refs.map((r) => r.fileId))
    await db.drafts.delete(draftId)
  })
}
